// Destinatarios de las anotaciones: regla pura compartida backend/frontend.
// Ver docs/partners-plan.md (D6-D7) y apps/backend/services/annotations/recipients.js,
// que añade la parte de Mongo.
//
// Tres lados: ClaimUp (`admin`), el cliente (company y su equipo, y el `client`
// legacy) y el despacho (`partner`, `partner_operator`).
// - `para` dice a quién le sale la nota como TAREA.
// - `oculta_al_cliente` dice si el cliente la puede LEER.
const { EXPEDIENT_TYPES } = require('../constants/expedient');
const { isPartnerRole } = require('../constants/roles');

const SIDES = { CLAIMUP: 'claimup', CLIENT: 'cliente', PARTNER: 'despacho' };

const SIDE_LABELS = {
  claimup: 'ClaimUp',
  cliente: 'Cliente',
  despacho: 'Despacho',
};

const CLIENT_SIDE_ROLES = ['company', 'client', 'operator', 'gestor'];

/** Lado al que pertenece un rol, o null si no escribe notas a mano. */
function sideOf(role) {
  if (role === 'admin') return SIDES.CLAIMUP;
  if (isPartnerRole(role)) return SIDES.PARTNER;
  if (CLIENT_SIDE_ROLES.includes(role)) return SIDES.CLIENT;
  return null;
}

/**
 * Qué destinatarios puede elegir un rol en un expediente, y cuáles se marcan
 * si no elige.
 *
 * El lado del cliente elige entre ClaimUp y su propia empresa
 * (docs/anotaciones-con-destinatario-plan.md): así se dejan tareas entre el
 * equipo. En un judicial, lo que va a ClaimUp va también al despacho, solo.
 * @returns {{ options: string[], defaults: string[] }}
 */
function recipientOptions(role, expedientType) {
  const judicial = expedientType === EXPEDIENT_TYPES.JUDICIAL;
  switch (sideOf(role)) {
    case SIDES.CLAIMUP:
      return {
        options: judicial ? [SIDES.CLIENT, SIDES.PARTNER] : [SIDES.CLIENT],
        defaults: judicial ? [SIDES.CLIENT, SIDES.PARTNER] : [SIDES.CLIENT],
      };
    case SIDES.PARTNER:
      return { options: [SIDES.CLAIMUP, SIDES.CLIENT], defaults: [SIDES.CLAIMUP] };
    case SIDES.CLIENT:
      // Los defaults son lo que la pantalla marca, así que solo pueden ser
      // opciones: el despacho no lo es para el cliente. Lo añade
      // resolveRecipients si la nota va a ClaimUp en un judicial.
      return { options: [SIDES.CLAIMUP, SIDES.CLIENT], defaults: [SIDES.CLAIMUP] };
    default:
      return { options: [], defaults: [] };
  }
}

/**
 * `para` y `oculta_al_cliente` de una nota nueva.
 *
 * Lo pedido se valida contra las opciones del rol; vacío o ausente = los de por
 * defecto. Si el lado del cliente la manda a ClaimUp en un judicial, va también
 * al despacho: no lo elige él.
 *
 * @returns {{ para: string[], oculta_al_cliente: true|undefined }}
 * @throws {Error} con `code: 'INVALID_RECIPIENT'` si pide uno que no puede elegir
 */
function resolveRecipients(role, expedientType, requested) {
  const side = sideOf(role);
  if (!side) {
    const error = new Error(`El rol ${role} no escribe anotaciones`);
    error.code = 'INVALID_RECIPIENT';
    throw error;
  }
  const { options, defaults } = recipientOptions(role, expedientType);

  let para = defaults;
  const asked = Array.isArray(requested) ? [...new Set(requested.map(String))] : [];
  if (asked.length > 0) {
    const invalid = asked.filter((r) => !options.includes(r));
    if (invalid.length > 0) {
      const error = new Error(`Destinatario no válido: ${invalid.join(', ')}`);
      error.code = 'INVALID_RECIPIENT';
      throw error;
    }
    para = asked;
  }
  if (side === SIDES.CLIENT && expedientType === EXPEDIENT_TYPES.JUDICIAL && para.includes(SIDES.CLAIMUP)) {
    para = [...para, SIDES.PARTNER];
  }

  // Solo ClaimUp y el despacho pueden dejar algo al margen del cliente.
  const oculta = side !== SIDES.CLIENT && !para.includes(SIDES.CLIENT);
  return { para, oculta_al_cliente: oculta ? true : undefined };
}

/**
 * A quién va una nota ya guardada. Las anteriores al 25/09/2026 no tienen
 * `para`: se leen con la regla de siempre (la escribe un admin → cliente; la
 * escribe cualquier otro, máquinas incluidas → ClaimUp). Es la misma regla que
 * aplica el backend en la consulta (services/annotations/recipients.js).
 */
function effectiveRecipients(note) {
  if (Array.isArray(note?.para) && note.para.length > 0) return note.para;
  return note?.creator_role === 'admin' ? [SIDES.CLIENT] : [SIDES.CLAIMUP];
}

/**
 * Qué roles escriben por cada lado. Las máquinas (`ai_agent`, `system`) no
 * escriben por ninguno: sus notas no «esperan» a nadie de parte de nadie.
 */
function rolesOfSide(side) {
  switch (side) {
    case SIDES.CLAIMUP:
      return ['admin'];
    case SIDES.PARTNER:
      return ['partner', 'partner_operator'];
    case SIDES.CLIENT:
      return [...CLIENT_SIDE_ROLES];
    default:
      return [];
  }
}

/**
 * Cómo se llama un destinatario según quién mira: para el lado del cliente,
 * «cliente» es su propia empresa.
 */
function sideLabelFor(side, viewerRole) {
  if (side === SIDES.CLIENT && sideOf(viewerRole) === SIDES.CLIENT) return 'Tu empresa';
  return SIDE_LABELS[side] || side;
}

/**
 * ¿Se puede dirigir una nota con estos destinatarios a una persona concreta?
 * Solo si va al lado del cliente: la persona es de la empresa del expediente.
 */
function canTargetUser(para) {
  return Array.isArray(para) && para.includes(SIDES.CLIENT);
}

/** ¿Puede este rol leer una nota oculta al cliente? */
function canSeeHiddenFromClient(role) {
  const side = sideOf(role);
  return side === SIDES.CLAIMUP || side === SIDES.PARTNER;
}

module.exports = {
  SIDES,
  SIDE_LABELS,
  sideOf,
  recipientOptions,
  resolveRecipients,
  sideLabelFor,
  canTargetUser,
  effectiveRecipients,
  rolesOfSide,
  canSeeHiddenFromClient,
};
